import { isCapacitorNative } from '@/lib/capacitor-runtime';

let splashHidden = false;

/** Hide Capacitor splash once web content is ready (or on fallback timeout). */
export async function hideNativeSplashScreen(): Promise<void> {
  if (!isCapacitorNative() || splashHidden) return;
  splashHidden = true;
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide({ fadeOutDuration: 200 });
  } catch {
    /* plugin unavailable */
  }
}

export function scheduleNativeSplashFallbackHide(timeoutMs = 3000): () => void {
  if (typeof window === 'undefined' || !isCapacitorNative()) {
    return () => {};
  }
  const timer = window.setTimeout(() => {
    void hideNativeSplashScreen();
  }, timeoutMs);
  return () => window.clearTimeout(timer);
}

export function bindNativeSplashHideOnContentReady(): () => void {
  if (typeof window === 'undefined' || !isCapacitorNative()) {
    return () => {};
  }

  const cancelFallback = scheduleNativeSplashFallbackHide(3000);

  const onReady = () => {
    void hideNativeSplashScreen();
  };

  const win = window as Window & { __ppcMarkContentReady?: () => void };
  const previous = win.__ppcMarkContentReady;
  const wrapped = () => {
    previous?.();
    onReady();
  };
  win.__ppcMarkContentReady = wrapped;

  if (document.body?.dataset.contentReady === '1') {
    onReady();
  }

  return () => {
    cancelFallback();
    if (win.__ppcMarkContentReady === wrapped) {
      win.__ppcMarkContentReady = previous;
    }
  };
}
